import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Search, Lightbulb, Wrench, Cog, TrendingUp,
  CheckCircle, Quote
} from 'lucide-react';

const methodSteps = [
  {
    number: '01',
    icon: Search,
    title: 'Diagnostic',
    description: 'Point de départ obligatoire. On analyse votre activité, votre niveau de digitalisation, votre parcours client et vos outils existants.',
    details: [
      'Compréhension de votre activité',
      'Audit de vos outils actuels',
      'Identification des points de friction',
      'Cartographie du parcours client',
    ],
    outcome: 'Vous avez une vision claire de votre situation réelle et des opportunités à saisir.',
  },
  {
    number: '02',
    icon: Lightbulb,
    title: 'Stratégie',
    description: 'On définit ensemble quels leviers activer, dans quel ordre, et avec quels objectifs business.',
    details: [
      'Définition des priorités',
      'Choix des solutions adaptées',
      'Planification des étapes',
      'Définition des KPIs',
    ],
    outcome: 'Vous avez une feuille de route claire, réaliste et priorisée.',
  },
  {
    number: '03',
    icon: Wrench,
    title: 'Mise en place',
    description: 'On implémente les solutions validées, étape par étape, avec vous ou pour vous selon le format choisi.',
    details: [
      'Configuration des outils',
      'Intégration des systèmes',
      'Tests et ajustements',
      'Formation si nécessaire',
    ],
    outcome: 'Vos nouveaux systèmes sont en place et fonctionnels.',
  },
  {
    number: '04',
    icon: Cog,
    title: 'Automatisation',
    description: 'On connecte les différentes briques et on automatise pour que le système tourne en autonomie.',
    details: [
      'Connexion des outils entre eux',
      'Création des workflows automatisés',
      'Mise en place des triggers',
      'Tests des scénarios',
    ],
    outcome: 'Le système travaille pour vous, même quand vous dormez.',
  },
  {
    number: '05',
    icon: TrendingUp,
    title: 'Optimisation',
    description: 'On suit les résultats et on ajuste en continu pour maximiser l\'impact et le ROI.',
    details: [
      'Suivi des métriques clés',
      'Analyse des performances',
      'Ajustements continus',
      'Recommandations d\'évolution',
    ],
    outcome: 'Vos systèmes s\'améliorent avec le temps et génèrent des résultats croissants.',
  },
];

const StepCard = ({ step, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative"
    >
      {/* Connection Line */}
      {index < methodSteps.length - 1 && (
        <div className="hidden lg:block absolute left-7 top-20 w-0.5 h-[calc(100%+4rem)] bg-gradient-to-b from-primary via-primary/30 to-transparent" />
      )}

      <div className="flex gap-8">
        {/* Number Badge */}
        <div className="shrink-0">
          <div className="number-badge relative z-10">
            {step.number}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 pb-16">
          <div className="p-8 rounded-2xl card-gradient border border-border/50 hover:border-primary/30 transition-all">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="feature-icon">
                <step.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-2xl font-bold text-foreground">
                {step.title}
              </h3>
            </div>

            {/* Description */}
            <p className="text-lg text-muted-foreground mb-6">
              {step.description}
            </p>

            {/* Details */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {step.details.map((detail) => (
                <div key={detail} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm text-foreground">{detail}</span>
                </div>
              ))}
            </div>

            {/* Outcome */}
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-primary">Résultat → </span>
                {step.outcome}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function MethodePage() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="hero-glow" />
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />

        <div className="section-container relative z-10">
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="section-label mb-4 inline-block">
              Notre Méthode
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Une approche{' '}
              <span className="gradient-text">structurée</span>
              {' '}et éprouvée
            </h1>
            <p className="text-lg text-muted-foreground">
              Pas d'improvisation. Chaque projet suit une méthodologie claire qui garantit 
              des résultats concrets et mesurables.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative p-8 lg:p-12 rounded-2xl card-gradient border border-border/50 max-w-4xl mx-auto"
          >
            <Quote className="w-12 h-12 text-primary/20 mb-6" />
            <blockquote className="text-xl lg:text-2xl text-foreground font-display leading-relaxed mb-6">
              "Tout commence par un diagnostic. On ne pousse jamais une solution sans 
              comprendre le besoin réel. Vous ne savez pas exactement ce qu'il vous faut ? 
              <span className="gradient-text"> C'est normal.</span> On commence par comprendre, 
              puis on construit."
            </blockquote>
            <p className="text-muted-foreground">
              — La philosophie PULSIA
            </p>
          </motion.div>
        </div>
      </section>

      {/* Method Steps */}
      <section className="py-20">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            {methodSteps.map((step, index) => (
              <StepCard key={step.number} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Why This Method */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="section-label mb-4 inline-block">
              Pourquoi cette approche
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Ce qui nous{' '}
              <span className="gradient-text">différencie</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Pas de vente forcée',
                desc: 'On ne vous pousse pas des solutions dont vous n\'avez pas besoin. Tout part de votre réalité.',
              },
              {
                title: 'Résultats mesurables',
                desc: 'Chaque action est liée à un objectif business concret. Pas de vanity metrics.',
              },
              {
                title: 'Accompagnement humain',
                desc: 'Vous n\'êtes pas seul. On vous guide et on s\'adapte à votre rythme.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl card-gradient border border-border/50 text-center"
              >
                <h3 className="font-display text-lg font-semibold text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-secondary/20 border border-border/50 p-8 lg:p-16 text-center"
          >
            <div className="hero-glow" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Prêt à commencer par l'étape 1 ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Le diagnostic est gratuit et sans engagement. C'est le meilleur moyen de savoir 
                exactement ce dont vous avez besoin.
              </p>
              <Link to="/contact">
                <Button variant="hero" size="xl" className="btn-glow group">
                  Demander mon diagnostic
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
